// Der Weg zur API der Plattform. JSON hin, JSON zurück; ein Fehler wirft ein
// `ApiError` mit dem Code des Servers — die Worte dazu stehen in lib/labels.ts.

import type { StepCheck } from "./types";

/** Was der Server im Fehlerfall mitschickt — `error` immer, der Rest je Route. */
export interface ApiErrorBody {
  error?: string;
  check?: StepCheck;
  /** `instance_error`: Status und Code der Instanz. */
  status?: number;
  code?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody | null;

  constructor(status: number, code: string, body: ApiErrorBody | null) {
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

let onUnauthorized: (() => void) | null = null;

/**
 * Was bei einer abgelaufenen Sitzung mitten im Arbeiten geschieht (Meldung,
 * dann neu laden) — gesetzt erst, wenn `/auth/me` einen Admin geliefert hat.
 * Vorher ist ein 401 die Antwort „nicht angemeldet", keine Störung.
 */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Eine Anfrage an `/api<path>`. Der Typ der Antwort ist eine Zusage des
 * Aufrufers (api/types.ts), keine Prüfung; 204 liefert `null`.
 */
export async function api<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });
  if (res.status === 204) return null as T;
  let data: unknown = null;
  try { data = await res.json(); } catch { /* kein JSON */ }
  if (!res.ok) {
    const errBody = data && typeof data === "object" ? (data as ApiErrorBody) : null;
    if (res.status === 401 && path !== "/auth/me" && onUnauthorized) onUnauthorized();
    throw new ApiError(res.status, errBody?.error ?? `http_${res.status}`, errBody);
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>("GET", path);
export const post = <T>(path: string, body: unknown = {}) => api<T>("POST", path, body);
export const put = <T>(path: string, body: unknown = {}) => api<T>("PUT", path, body);
export const patch = <T>(path: string, body: unknown = {}) => api<T>("PATCH", path, body);
export const del = <T>(path: string, body?: unknown) => api<T>("DELETE", path, body);
